import { memo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { Button, CircularLoadingButton } from '@mint-lib/tw-components';

import { useIntegrationAccountTypeRouteApi } from '../../../hooks/useIntegrationsRouteApi.js';
import { useSelectedIntegration } from '../../../hooks/useSelectedIntegration.js';
import { useSelectedPlatform } from '../../../hooks/useSelectedPlatform.js';
import { getInitValues } from '../AccountConnectForm/AccountForm.utils.jsx';

type SaveButtonsType = {
  label?: string;
  option?: { [key: string]: string };
  isIntegrateType?: boolean;
  integrationId?: string;
  isUserReadOnly: boolean;
  isSubmitDisabled?: boolean;
  isLoading?: boolean;
  onCancelClick?: () => void;
  onActionClick?: () => void;
  isCancel?: boolean;
  handleFormSubmit?: (values: { [key: string]: unknown }) => void;
};

const SaveButtons = ({
  label,
  option,
  isIntegrateType,
  integrationId,
  isUserReadOnly,
  isLoading = false,
  onCancelClick,
  onActionClick,
  isSubmitDisabled,
  isCancel = true,
  handleFormSubmit,
}: SaveButtonsType) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const { integration: integrationParam, type } =
    useIntegrationAccountTypeRouteApi().useParams();
  const formContext = useFormContext();

  const values = formContext?.watch() as {
    [key: string]: unknown;
  };

  const dirty = formContext.formState.isDirty;

  const { selectedIntegration } = useSelectedIntegration();
  const selectedPlatform = useSelectedPlatform();
  const integration = selectedPlatform?.isOmni ? 'omni' : integrationParam;

  const hasErrors = Object.values(formContext.formState.errors).some((item) => {
    if (item) {
      return true;
    }
    return false;
  });

  const setInitValues = () => {
    const initValues = getInitValues(
      selectedIntegration,
      type ?? 'add',
      integration,
    );
    formContext.reset(initValues);
  };

  const handleCancelClick = () => {
    if (onCancelClick) {
      onCancelClick();
    } else {
      setInitValues();
    }
  };
  const handleSubmitClick = () => {
    if (onActionClick) {
      return onActionClick();
    }
    if (handleFormSubmit) {
      return handleFormSubmit(values);
    }
  };

  return (
    <div className="flex justify-end gap-2">
      {isCancel && (
        <Button
          variant="text"
          size="large"
          type="button"
          onClick={handleCancelClick}
          disabled={!dirty || isUserReadOnly}
          data-dd-action-name="cancel integration form"
        >
          {t('Cancel')}
        </Button>
      )}

      <Button
        disabled={isSubmitDisabled ?? (!dirty || hasErrors || isUserReadOnly)}
        variant="contained"
        size="large"
        type="submit"
        onClick={handleSubmitClick}
        data-dd-action-name="submit integration form"
      >
        {isLoading && (
          <CircularLoadingButton size="small" variant="contained" />
        )}
        {label === 'add' ? t('Integrate') : label}
      </Button>
    </div>
  );
};

export default memo(SaveButtons);
